// SPDX-License-Identifier: AGPL-3.0-or-later

import {getGameIconUrl} from '@app/features/game_activity/utils/GameIconUrls';
import type {GameActivity} from '@app/features/gateway/types/GatewayPresenceTypes';
import {getGameById} from '@fluxer/game_catalog/src/GameCatalog';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {useEffect, useState} from 'react';

const PLAYING = msg({
	message: 'Playing {name}',
	comment: 'Text and accessible label for the current game shown under a name in the member and direct message lists.',
});
const GAME = msg({message: 'Game', comment: 'Fallback name for an unknown game ID.'});

export function useGameActivityIcon(activity: GameActivity): {label: string; iconUrl: string | null} {
	const {i18n} = useLingui();
	const gameId = 'game_id' in activity ? activity.game_id : null;
	const game = gameId ? getGameById(gameId) : undefined;
	const name = 'manual_name' in activity ? activity.manual_name : (game?.name ?? i18n._(GAME));
	const label = i18n._({...PLAYING, values: {name}});
	const [icon, setIcon] = useState<{id: string; url: string} | null>(null);
	useEffect(() => {
		if (!gameId || !game?.icon) return;
		let cancelled = false;
		void getGameIconUrl(gameId, 64)
			.then((url) => {
				if (!cancelled) setIcon(url ? {id: gameId, url} : null);
			})
			.catch(() => {
				if (!cancelled) setIcon(null);
			});
		return () => {
			cancelled = true;
		};
	}, [gameId, game?.icon]);
	return {label, iconUrl: icon && icon.id === gameId ? icon.url : null};
}
